import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { SignalRService } from 'core/signalR-service/signal-r.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { SignalRUpload } from 'src/app/modules/shared/entities/signalr-upload.class';
import { Product } from '../../modules/pos/quotes/entities/products.class';
import { QuoteToolsService } from '../../modules/pos/quotes/services/quote-tools.service';
import { SignalrConnectionType } from '../../modules/shared/entities/signalrConnectionType.enum';
import { SignalrMethodName } from '../../modules/shared/entities/signalrMethodName.enum';
import { SignalrNotification } from '../../modules/shared/entities/SignalrNotification.class';
import { SignalrProgressState } from '../../modules/shared/entities/signalrProgressState.enum';
import { TooltipNotification } from './tooltip-notification.class';
import { TooltipNotificationService } from './tooltip-notification.service';

@Component({
  selector: 'app-tooltip-notification',
  templateUrl: './tooltip-notification.component.html',
  styleUrls: ['./tooltip-notification.component.scss'],
  standalone: false
})

/**
 * Component that will display a "tooltip" html menu for items
 * Will be used in the product notification list and warehouse functionality
 */
export class TooltipNotificationComponent implements OnInit {
  private subscriptions = new Subscription();
  tooltipNotifications: TooltipNotification;
  selectedTool: Product;
  selectedToolIndex: number;
  currencySymbol: any;
  formatDate = this.appInfoService.dateFormat;
  // will only be set if the oem sku value will be changed
  changedOemSkuValue;
  selection;
  appStrings: AppLocalization;

  @Output() buttonCallback = new EventEmitter();
  @Input() canEdit: boolean;

  constructor(
    private tooltipNotificationService: TooltipNotificationService,
    private appInfoService: AppInfoService,
    private quoteToolsService: QuoteToolsService,
    private signalrService: SignalRService,
    private notificationService: NotificationService,
  ) { }

  ngOnInit() {
    this.selectedTool = null;
    this.tooltipNotifications = new TooltipNotification();
    this.appStrings = this.appInfoService.appStrings;
    this.currencySymbol = this.appInfoService.currencySymbol;
    this.subscriptions.add(this.signalrService.itemizeSetProgressReceived$
      .subscribe((itemizeSetProgress: SignalRUpload) => this.setItemizeSetProgress(itemizeSetProgress)
      ));
  }

  toggleOemSkuRadio(event) {
    this.changedOemSkuValue = event.value;
  }

  showTooltipNotification(tool: Product, toolIndex, templateValues) {
    this.selection = null;
    this.changedOemSkuValue = null;
    this.selectedTool = tool;
    this.selectedToolIndex = toolIndex;
    this.tooltipNotifications = this.tooltipNotificationService.parseToolNotifications(tool, templateValues);
  }

  handleNotification(notification, button, callback?) {
    const extraParams: any = {};
    if (button.callbackType === 'oemSku') {
      // there's going to be only one notification, and it's going to be an oem sku one
      extraParams.oemSkuValue = this.changedOemSkuValue || this.selectedTool.notifications[0].oemSkuProductNumbers[0];
    }

    this.buttonCallback.emit({
      tool: this.selectedTool,
      toolIndex: this.selectedToolIndex,
      notification,
      button,
      extraParams
    });
    if (callback) {
      callback();
    }
  }

  itemizeAction(indexChoice, notification) {
    const choice = Number(indexChoice);
    const options = {
      1: () => this.handleNotification(notification, { callbackType: 'ignore' }),
      2: () => this.itemizeSet(notification),
      3: () => this.tooltipNotificationService.keepAsSetAndItemizeLineNotes(this.selectedTool, notification.id)
    };
    const action = options[choice];
    action();
  }

  private itemizeSet(notification) {
    this.selectedTool.isLoading = true;
    this.quoteToolsService.itemizeInProgress$.next(true);
    this.signalrService.startConnection(SignalrConnectionType.ItemizeSet)
      .then(() => {
        const signalrNotification = new SignalrNotification(
          this.signalrService.connectionId,
          null,
          SignalrMethodName.itemizeSet
        );

        const payload = {
          id: notification.id,
          notification: signalrNotification
        };
        this.tooltipNotificationService.itemizeSet(payload)
          .subscribe(() => {
            // This is intentional
          }, () => {
            this.selectedTool.isLoading = false;
            this.quoteToolsService.itemizeInProgress$.next(false);
          });
      });
  }

  usePromoPrice(notificationId) {
    this.selectedTool.isLoading = true;
    this.tooltipNotificationService.usePromoPrice(notificationId).subscribe(
      promoObject => {
        this.selectedTool.isLoading = false;
        this.selectedTool.netPrice = promoObject.netPrice;
        this.selectedTool.listPrice = promoObject.listPrice;
        this.selectedTool.total = promoObject.total;
        this.selectedTool.notifications = promoObject.notifications;
        this.selectedTool.notificationIcon = this.quoteToolsService.getNotificationIconForProduct(this.selectedTool);
        this.quoteToolsService.productListHasChanges$.next(true);
      },
      () => this.selectedTool.isLoading = false);
  }

  useCurrentPrice(notification) {
    this.selectedTool.notifications = [];
    this.selectedTool.isLoading = true;
    this.selectedTool.netPrice = this.selectedTool.listPrice;
    this.selectedTool.total = this.selectedTool.listPrice.toString();
    const event = {
      tool: this.selectedTool,
      notification
    };

    this.quoteToolsService.ignoreNotification(event, () => {
      this.quoteToolsService.editTool(this.selectedTool, null, () => {
        // This is intentional
      }, param => {
        this.checkIfManuallyNotif(param);
        this.selectedTool.isLoading = false;
      });
    });
  }

  checkIfManuallyNotif(tool) {
    tool.notifications.forEach(item => {
      if (item.description.indexOf('manually') !== -1) {
        const event = {
          tool,
          notification: item
        };
        this.quoteToolsService.ignoreNotification(event);
      }
    });
  }

  private setItemizeSetProgress(progress: SignalRUpload) {
    if (progress.state === SignalrProgressState.Done) {
      this.actionAfterSetItemized();
    }
    if (progress.state === SignalrProgressState.Error) {
      this.selectedTool.isLoading = false;
      this.signalrService.closeConnection();
      this.quoteToolsService.itemizeInProgress$.next(false);
      this.notificationService.showNotification(progress.message, NotificationType.Error, true);
    }
  }

  private actionAfterSetItemized() {
    this.selectedTool.isLoading = false;
    this.signalrService.closeConnection();
    this.quoteToolsService.refreshToolsList$.next(() => this.selectedTool.isLoading = false);
    this.quoteToolsService.itemizeInProgress$.next(false);
    this.quoteToolsService.checkIfNotLeadAndUpdateSummary();
  }
}
